import * as appHelper from './appHelper';
import * as colourConstants from '../constants/colourConstants';
import * as colourModels from '../models/colourModels';

export function hexToRgb(hex) {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    let shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function (m, r, g, b) {
        return r + r + g + g + b + b;
    });

    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
}

export function hexToRgba(hex, alpha) {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    let shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function (m, r, g, b) {
        return r + r + g + g + b + b;
    });

    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    let r = parseInt(result[1], 16);
    let g = parseInt(result[2], 16);
    let b = parseInt(result[3], 16);
    return result ? `rgba(${r},${g},${b},${alpha})` : null;
}

export function rgbToHex(r, g, b) {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

export function colorLuminance(hex, lum) {

    // validate hex string
    hex = String(hex).replace(/[^0-9a-f]/gi, '');
    if (hex.length < 6) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    lum = lum || 0;

    // convert to decimal and change luminosity
    let rgb = "#", c, i;
    for (i = 0; i < 3; i++) {
        c = parseInt(hex.substr(i * 2, 2), 16);
        c = Math.round(Math.min(Math.max(0, c + (c * lum)), 255)).toString(16);
        rgb += ("00" + c).substr(c.length);
    }

    return rgb;
}

export function getColourShade(color, percent) {
    if(typeof color === "string") {
        let f = parseInt(color.slice(1), 16), t = percent < 0 ? 0 : 255, p = percent < 0 ? percent * -1 : percent,
            R = f >> 16, G = f >> 8 & 0x00FF, B = f & 0x0000FF;

        return "#" + (0x1000000 + (Math.round((t - R) * p) + R) * 0x10000 + (Math.round((t - G) * p) + G) * 0x100 + (Math.round((t - B) * p) + B)).toString(16).slice(1);
    }else{
        return color;
    }
}

export function getDarkAndLightColourSwatchRange(colour, constant) {

    let colour_80_darker = getColourShade(colour, -0.8);
    let colour_60_darker = getColourShade(colour, -0.6);
    let colour_40_darker = getColourShade(colour, -0.4);
    let colour_20_darker = getColourShade(colour, -0.2);
    let colour_20_lighter = getColourShade(colour, 0.2);
    let colour_40_lighter = getColourShade(colour, 0.4);
    let colour_60_lighter = getColourShade(colour, 0.6);
    let colour_80_lighter = getColourShade(colour, 0.8);

    let colourSwatches = [
        {
            constant: `${constant}|80_darker`,
            label: colour_80_darker,
            value: colour_80_darker,
        },
        {
            constant: `${constant}|60_darker`,
            label: colour_60_darker,
            value: colour_60_darker,
        },
        {
            constant: `${constant}|40_darker`,
            label: colour_40_darker,
            value: colour_40_darker,
        },
        {
            constant: `${constant}|20_darker`,
            label: colour_20_darker,
            value: colour_20_darker,
        },
        {
            constant: constant,
            label: colour,
            value: colour,
        },
        {
            constant: `${constant}|20_lighter`,
            label: colour_20_lighter,
            value: colour_20_lighter,
        },
        {
            constant: `${constant}|40_lighter`,
            label: colour_40_lighter,
            value: colour_40_lighter,
        },
        {
            constant: `${constant}|60_lighter`,
            label: colour_60_lighter,
            value: colour_60_lighter,
        },
        {
            constant: `${constant}|80_lighter`,
            label: colour_80_lighter,
            value: colour_80_lighter,
        }
    ];

    return colourSwatches;
}

export function getDarkToLightColourSwatchRange(colour, constant) {
    let colour_10_lighter = getColourShade(colour, 0.1);
    let colour_20_lighter = getColourShade(colour, 0.2);
    let colour_30_lighter = getColourShade(colour, 0.3);
    let colour_40_lighter = getColourShade(colour, 0.4);
    let colour_50_lighter = getColourShade(colour, 0.5);
    let colour_60_lighter = getColourShade(colour, 0.6);
    let colour_70_lighter = getColourShade(colour, 0.7);
    let colour_80_lighter = getColourShade(colour, 0.8);
    let colour_90_lighter = getColourShade(colour, 0.9);
    let colour_100_lighter = getColourShade(colour, 1);
    let colourSwatches = [
        {
            constant: constant,
            label: colour,
            value: colour,
        },
        {
            constant: `${constant}|10_lighter`,
            label: colour_10_lighter,
            value: colour_10_lighter,
        },
        {
            constant: `${constant}|20_lighter`,
            label: colour_20_lighter,
            value: colour_20_lighter,
        },
        {
            constant: `${constant}|30_lighter`,
            label: colour_30_lighter,
            value: colour_30_lighter,
        },
        {
            constant: `${constant}|40_lighter`,
            label: colour_40_lighter,
            value: colour_40_lighter,
        },
        {
            constant: `${constant}|50_lighter`,
            label: colour_50_lighter,
            value: colour_50_lighter,
        },
        {
            constant: `${constant}|60_lighter`,
            label: colour_60_lighter,
            value: colour_60_lighter,
        },
        {
            constant: `${constant}|70_lighter`,
            label: colour_70_lighter,
            value: colour_70_lighter,
        },
        {
            constant: `${constant}|80_lighter`,
            label: colour_80_lighter,
            value: colour_80_lighter,
        },
        {
            constant: `${constant}|90_lighter`,
            label: colour_90_lighter,
            value: colour_90_lighter,
        },
        {
            constant: `${constant}|100_lighter`,
            label: colour_100_lighter,
            value: colour_100_lighter
        }
    ];
    return colourSwatches;
}

export function getColourValue(value) {
    if (typeof value === "string") {
        if (value === colourConstants.INHERIT_BUTTON_COLOR_FROM_THEME) {
            if (colourModels.button_color !== "" && colourModels.button_color !== colourConstants.INHERIT_BUTTON_COLOR_FROM_THEME) {
                return colourModels.button_color;
            } else {
                return colourModels.primary_color;
            }
        }

        if (value === colourConstants.INHERT_OVERLAY_COLOUR_FROM_THEME) {
            if (appHelper.isDefined(colourModels.background_layer_color) && colourModels.background_layer_color !== "") {
                return colourModels.background_layer_color;
            } else {
                return colourModels.primary_color;
            }
        }

        if (value === colourConstants.INHERT_TEXT_COLOUR_FROM_THEME) {
            if (appHelper.isDefined(colourModels.titles_and_sections_color) && colourModels.titles_and_sections_color !== "") {
                return colourModels.titles_and_sections_color;
            } else {
                return colourModels.primary_color;
            }
        }

        let valueArray = value.split("|");
        let colour_swatch = appHelper.getArrayObjectByKey(colourModels.colour_swatches, "constant", valueArray[0]);

        if (appHelper.isDefined(colour_swatch)) {
            let color = appHelper.getArrayObjectByKey(colour_swatch.colours, "constant", value);
            if (appHelper.isDefined(color.value)) {
                return color.value;
            } else {
                //return default
                return colourModels.primary_color;
            }
        } else {
            return value;
        }
    } else {
        if (appHelper.isDefined(value.r)) {
            if (appHelper.isDefined(value.a)) {
                return `rgba(${value.r},${value.g},${value.b},${value.a})`;
            } else {
                return `rgb(${value.r},${value.g},${value.b})`;
            }
        } else {
            //return default
            return colourModels.primary_color;
        }

    }
}

export function getColourValueString(value){
    if (typeof value === "string") {
        return value;
    } else if (appHelper.isDefined(value.r)) {
        if (appHelper.isDefined(value.a)) {
            return `rgba(${value.r},${value.g},${value.b},${value.a})`;
        } else {
            return `rgb(${value.r},${value.g},${value.b})`;
        }
    }
}


/** WEBPACK FOOTER **
 ** ./src/helpers/colourHelper.js
 **/